import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Lock, Zap } from "lucide-react";
import { api, ghs } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ProductImage } from "../../components/site/ProductCard";
import { EmptyState } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";

export default function ProductDetail({ base = "/products" }) {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
    if (user) api.get("/me/purchases").then((r) => setPurchase(r.data.find((x) => x.item_id === id) || null)).catch(() => {});
  }, [id, user]);

  if (missing) return <div className="mx-auto max-w-3xl px-5 py-20"><EmptyState title="Product not found" text="This product may have been removed." action={<Link to={base} className="btn-ghost">Back to products</Link>} /></div>;
  if (!p) return <FullLoader />;

  const buy = () => (user ? nav(`/checkout/product/${p.id}`) : nav(`/login?next=/checkout/product/${p.id}`));

  return (
    <div className={base === "/products" ? "mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16" : ""}>
      <Link to={base} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="product-back-link"><ArrowLeft size={16} /> All products</Link>
      <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="card-rx aspect-[4/3] overflow-hidden"><ProductImage path={p.image_path} alt={p.name} /></div>
        <div className="rise">
          <h1 className="text-3xl font-extrabold text-white sm:text-4xl" data-testid="product-detail-name">{p.name}</h1>
          <p className="mt-3 text-slate-400">{p.short_description}</p>
          <p className="mt-6 font-mono-rx text-3xl font-semibold text-amber-400" data-testid="product-detail-price">{ghs(p.price_minor)}</p>
          {!p.is_available && <p className="mt-3 text-sm text-red-300">Currently unavailable</p>}
          <div className="mt-8">
            {purchase ? (
              <Link to={`/purchases/${purchase.id}`} className="btn-primary w-full sm:w-auto" data-testid="product-open-purchase-btn"><CheckCircle2 size={16} /> Purchased — open product</Link>
            ) : (
              <button onClick={buy} disabled={!p.is_available} className="btn-primary w-full disabled:opacity-50 sm:w-auto" data-testid="product-buy-btn">
                <Lock size={16} /> Buy now
              </button>
            )}
          </div>
          <div className="mt-6 flex flex-wrap gap-5 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Zap size={14} className="text-emerald-400" /> Instant access after verified payment</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={14} className="text-emerald-400" /> Secure checkout via theteller</span>
          </div>
          {p.description && (
            <div className="mt-10 border-t border-white/[0.06] pt-8">
              <h2 className="text-lg font-semibold text-white">Product details</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-300" data-testid="product-detail-description">{p.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
